import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import UomSelect from "@/components/UomSelect";
import BulkUploadButton from "@/components/BulkUploadButton";

const empty = { name: "", unit: "kg", unit_other: "", price_per_unit: 0, notes: "" };

export default function Materials() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = async () => { setList((await api.get("/materials")).data); };
    useEffect(() => { load(); }, []);

    const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
    const startEdit = (m) => { setEditing(m); setForm({ ...m }); setOpen(true); };

    const save = async () => {
        if (!form.name.trim()) { toast.error("Name required"); return; }
        const payload = { ...form, price_per_unit: Number(form.price_per_unit) || 0 };
        try {
            if (editing) await api.put(`/materials/${editing.material_id}`, payload);
            else await api.post("/materials", payload);
            setOpen(false); await load();
            toast.success("Saved");
        } catch { toast.error("Save failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete material?")) return;
        await api.delete(`/materials/${id}`); await load();
    };

    return (
        <div>
            <PageHeader
                eyebrow="Master" title="Materials"
                subtitle="Material catalogue with unit price. Use in BOM material cost lines."
                actions={
                    <div className="flex gap-2">
                        <BulkUploadButton entity="materials" onComplete={load} />
                        <Button onClick={startCreate} className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" data-testid="new-material-button"><Plus size={16} className="mr-2" /> Add Material</Button>
                    </div>
                }
            />
            <div className="px-8 py-6">
                {list.length === 0 ? (
                    <Empty onCreate={startCreate} label="materials" />
                ) : (
                    <table className="w-full sheet-table" data-testid="materials-table">
                        <thead>
                            <tr><th>Material</th><th>Unit</th><th>Price / Unit (₹)</th><th>Notes</th><th></th></tr>
                        </thead>
                        <tbody>
                            {list.map((m) => (
                                <tr key={m.material_id} data-testid={`material-row-${m.material_id}`}>
                                    <td className="font-medium">{m.name}</td>
                                    <td className="font-mono">{m.unit}</td>
                                    <td className="font-mono">{m.price_per_unit}</td>
                                    <td className="text-zinc-500 text-xs">{m.notes}</td>
                                    <td className="text-right w-32">
                                        <button onClick={() => startEdit(m)} className="text-[#002FA7] hover:underline text-xs uppercase tracking-wider">Edit</button>
                                        <button onClick={() => remove(m.material_id)} className="ml-3 text-zinc-400 hover:text-[#E63946]"><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-none max-w-lg">
                    <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Material</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Material Name *"><Input data-testid="material-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        <Field label="Unit">
                            <UomSelect
                                unit={form.unit || ""}
                                unitOther={form.unit_other || ""}
                                onChange={(patch) => setForm({ ...form, ...patch })}
                                testIdPrefix="material-master-uom"
                            />
                        </Field>
                        <Field label="Price per Unit (₹)"><Input type="number" data-testid="material-price-input" value={form.price_per_unit} onChange={(e) => setForm({ ...form, price_per_unit: e.target.value })} /></Field>
                        <div className="col-span-2">
                            <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" onClick={save} data-testid="save-material-button">{editing ? "Update" : "Create"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, children }) {
    return <div className="space-y-1.5"><Label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>{children}</div>;
}
function Empty({ onCreate, label }) {
    return (
        <div className="border-2 border-dashed border-zinc-300 p-16 text-center">
            <div className="tag-pill mb-4 inline-flex">Empty</div>
            <h2 className="text-2xl font-bold tracking-tight">No {label} yet</h2>
            <Button onClick={onCreate} className="mt-6 rounded-none bg-[#002FA7] hover:bg-[#00227B]">
                <Plus size={16} className="mr-2" /> Add now
            </Button>
        </div>
    );
}
